import React, { useContext, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { isNotBlank, isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon.tsx';
import { ColWithGap } from '@components/styles.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgNoteInfo from '@components/ringil3/icons/NoteInfo';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilTextArea from '@components/framework/input/RingilTextArea.tsx';
import InputDateAndTime from '@components/InputDateAndTime/InputDateAndTime.tsx';
import useBEValidation from '@hooks/useBEValidation/useBEValidation.tsx';
import dayjs from 'dayjs';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

interface SelectedCarrier {
  carrierCompanyId: string;
  carrierName: string;
}

interface SidebarData {
  distributionId: string;
  applicationId: string;
  carriers: SelectedCarrier[];
}

interface ComponentProps {
  onCompleted: () => void;
}

export const useCreateCarrierTiebreak = () => {
  const [sidebarData, setSidebarData] = useState<SidebarData>(undefined);

  const CarrierTiebreakSidebar: React.FC<ComponentProps> = ({ onCompleted }) => {
    const { tr } = useTranslationLgs();
    const { distributionCreateTiebreaker } = useContext(ApiContext);
    const { loggedUser } = useContext(AuthenticatedUserContext);
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();

    const [deadline, setDeadline] = useState<dayjs.Dayjs>(dayjs());
    const [timezoneId, setTimezoneId] = useState<string>(loggedUser?.defaultTimezoneId || 'Europe/Prague');
    const [message, setMessage] = useState<string>('');
    const [loading, setLoading] = useState(false);

    const reset = () => {
      setDeadline(dayjs());
      setTimezoneId(loggedUser?.defaultTimezoneId || 'Europe/Prague');
      setMessage('');
      setLoading(false);
    };

    const close = () => {
      reset();
      setSidebarData(null);
    };

    const submit = () => {
      if (isNullOrUndefined(deadline) || isNullOrUndefined(timezoneId)) return;
      setLoading(true);
      distributionCreateTiebreaker(
        sidebarData.distributionId,
        {
          carrierCompanyIds: sidebarData.carriers.map(c => c.carrierCompanyId),
          utcEnd: deadline.toISOString(),
          timezoneId: timezoneId,
          message: isNotBlank(message) ? message : undefined,
        },
        () => {
          setLoading(false);
          onCompleted();
          close();
        },
        null,
        {
          onValidationFailed: errors => {
            setLoading(false);
            setBeValidationErrors(errors);
          },
          onError: (_error, showStandardError) => {
            showStandardError();
            setLoading(false);
          },
        },
      );
    };

    const carrierNames = sidebarData?.carriers?.map(c => c.carrierName).join(' | ');

    return (
      <RingilSidebar
        visible={isNotNullOrUndefined(sidebarData)}
        heading={tr('CarrierTiebreakSidebar.heading', 'Carrier tiebreak')}
        onContinue={() => submit()}
        onBack={() => close()}
        loading={loading}
        disableSaveButton={isNullOrUndefined(deadline) || !isNotBlank(timezoneId)}
        dataTestIdContinue="expedition-carrierTiebreak-continue"
        dataTestIdBack="expedition-carrierTiebreak-back"
      >
        <ColWithGap>
          <BackendValidationComponent />

          <VerticalFieldAndValueWithIcon
            label={tr('CarrierTiebreakSidebar.forExpedition', 'For expedition')}
            valueText={sidebarData?.applicationId}
            iconPath={'/images/icons/ringil3/forExpedition.svg'}
          />

          <VerticalFieldAndValueWithIcon
            label={tr('CarrierTiebreakSidebar.selectedCarriers', 'Selected carriers')}
            valueText={carrierNames}
            iconPath={'/images/icons/ringil3/trucktype.svg'}
            showHr={true}
          />

          <COInlineMessage icon={<SvgNoteInfo />} variant={'info'}>
            {tr(
              'CarrierTiebreakSidebar.infoMessage',
              'Use carrier tiebreak when two or more carriers offered the same price (compared in your default company currency). Selected carriers will be invited to submit a new, improved offer before the deadline. The lowest binding offer wins.',
            )}
          </COInlineMessage>

          <Field
            label={tr('CarrierTiebreakSidebar.deadline', 'Tiebreak deadline')}
            required={isNullOrUndefined(deadline)}
          >
            <InputDateAndTime
              value={deadline}
              timeZone={timezoneId}
              onChange={(localDate, tz) => {
                setDeadline(localDate as dayjs.Dayjs);
                if (tz) setTimezoneId(tz);
              }}
              fullWidth
              quickPrefills={[
                { value: 15, unit: 'minute', label: tr('CarrierTiebreakSidebar.plus15Minutes', '+15 minutes') },
                { value: 30, unit: 'minute', label: tr('CarrierTiebreakSidebar.plus30Minutes', '+30 minutes') },
              ]}
              dataTestIdCalendar="expedition-carrierTiebreak-deadline-calendar"
              dataTestIdTimezone="expedition-carrierTiebreak-deadline-timezone"
            />
          </Field>

          <Field label={tr('CarrierTiebreakSidebar.messageToCarriers', 'Message to carriers')}>
            <RingilTextArea
              value={message}
              onChange={v => setMessage(v)}
              maxLength={1000}
              dataTestId="expedition-carrierTiebreak-message"
            />
          </Field>
        </ColWithGap>
      </RingilSidebar>
    );
  };

  return { CarrierTiebreakSidebar, setSidebarData };
};